import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Job } from '@sciper/api-client'
import {
  BehaviorSubject,
  first,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs'
import { environment } from 'src/environments/environment'
import { AbstractChatService } from '../abstract-chat/abstract-chat.service'
import { RestServiceStatus } from '../abstract-rest/abstract-rest.service'
import { AuthService } from '../auth/auth.service'
import { ChatUnreadMessageService } from '../chat-unread-message/chat-unread-message.service'
import { JobService } from '../job/job.service'
import {
  UniversityService,
  UniversityUserList,
} from '../university/university.service'
import {
  ChatRoom,
  ChatRoomField,
  ChatRoomNameData,
  GroupedChatRooms,
} from './chat-room.fixtures'

@Injectable({
  providedIn: 'root',
})
export class ChatRoomService extends AbstractChatService {
  private refetchChatRooms: boolean = false
  private refetchChatRoomId: string = undefined

  readonly chatRooms: BehaviorSubject<ChatRoom[]> = new BehaviorSubject([])
  readonly chatRoom: BehaviorSubject<ChatRoom> = new BehaviorSubject(undefined)
  readonly leftChatRoom: BehaviorSubject<string> = new BehaviorSubject(
    undefined
  )

  constructor(
    private httpClient: HttpClient,
    private unreadMessagesService: ChatUnreadMessageService,
    private jobService: JobService,
    private universityService: UniversityService,
    authService: AuthService
  ) {
    super(authService)
  }

  override didFetchUserData(): void {
    if (this.refetchChatRooms) {
      this.fetchChatRooms()
    }
    if (this.refetchChatRoomId) {
      this.fetchChatRoom(this.refetchChatRoomId)
    }
  }

  fetchChatRooms() {
    if (!this.chatUserData) {
      this.refetchChatRooms = true
      return
    }

    this.status.next(RestServiceStatus.Loading)
    const url = environment.chatBasePath + '/rooms.get'
    this.httpClient
      .get<any>(url, { headers: this.getAuthHeaders() })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const chatRooms: ChatRoom[] = response.update.map((room: any) => {
            return ChatRoom.fromApiRoom(room)
          })
          this.chatRooms.next(chatRooms)
          if (this.chatRoom.value) {
            const updatedChatRoom = chatRooms.find(
              (r) => r.id === this.chatRoom.value.id
            )
            if (this.chatRoom) {
              this.chatRoom.next(updatedChatRoom)
            }
          }
          this.status.next(RestServiceStatus.Success)
        },
        error: (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
        },
      })
  }

  fetchChatRoom(roomId: string, forceReload: boolean = false) {
    if (!this.chatUserData) {
      this.refetchChatRoomId = roomId
      return
    }
    if (!forceReload) {
      const cachedRoom = this.chatRooms.value.find((room) => room.id === roomId)
      if (cachedRoom) {
        this.chatRoom.next(cachedRoom)
        return
      }
    }

    this.status.next(RestServiceStatus.Loading)
    const url = environment.chatBasePath + '/rooms.info?roomId=' + roomId
    this.httpClient
      .get<any>(url, { headers: this.getAuthHeaders() })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response.room) {
            const chatRoom = ChatRoom.fromApiRoom(response.room)
            if (chatRoom) {
              this.chatRoom.next(chatRoom)
            }
          }
          this.status.next(RestServiceStatus.Success)
        },
        error: (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
        },
      })
  }

  fetchChatRoomMembers(roomId: string): Observable<{
    success: boolean
    count: number
    members?: [
      {
        name: string
        username: string
        _id: string
      },
    ]
  }> {
    if (!this.chatUserData) {
      return of({ success: false, count: 0 })
    }

    const url = environment.chatBasePath + '/channels.members?roomId=' + roomId
    const resultObservable = this.httpClient.get<any>(url, {
      headers: this.getAuthHeaders(),
    })
    return resultObservable
  }

  createChatRoom(
    job: Job
  ): Observable<{ success: boolean; chatRoomId: string }> {
    if (!this.chatUserData) {
      return of({ success: false, chatRoomId: undefined })
    }
    const nameData: ChatRoomNameData = {
      name: job.title,
      uuid: job.jobId,
      userId: this.chatUserData.userId,
    }
    const encodedName = ChatRoom.encodeChatRoomData(nameData)
    const customFields: {
      [key: string]: string
    } = {}
    customFields[ChatRoomField.jobId] = job.jobId

    const observable = this.loadUniversityUsers(job.universityId).pipe(
      switchMap((universityUserList) => {
        let members: string[] = []
        if (universityUserList?.users?.length) {
          members = universityUserList.users.map((user) => user.userId)
        }
        if (members.length === 0) {
          members = (job.publisherId?.length ?? 0) > 0 ? [job.publisherId] : []
        }
        const data = {
          name: encodedName,
          members: members,
          customFields: customFields,
        }
        return this.createChatRoomWithData(data)
      })
    )
    return observable
  }

  fetchRoomId(
    job: Job
  ): Observable<{ success: boolean; channel?: { _id: string } }> {
    if (!this.chatUserData) {
      return of({ success: false, chatRoomId: undefined })
    }
    const nameData: ChatRoomNameData = {
      name: job.title,
      uuid: job.jobId,
      userId: this.chatUserData.userId,
    }
    const url =
      environment.chatBasePath +
      '/channels.info?roomName=' +
      ChatRoom.encodeChatRoomData(nameData)
    const resultObservable = this.httpClient.get<any>(url, {
      headers: this.getAuthHeaders(),
    })
    return resultObservable
  }

  getRocketChatUserId(apiUserId: string) {
    const url = environment.chatBasePath + '/users.info?username=' + apiUserId
    const resultObservable = this.httpClient.get<any>(url, {
      headers: this.getAuthHeaders(),
    })
    resultObservable.subscribe((result) => console.log(result))
    return resultObservable
  }

  addUsersToRoom(
    roomId: string,
    userIds: string[],
    loadRocketChatUserId: boolean
  ): Observable<{ success: boolean }> {
    if (!loadRocketChatUserId) {
      return this.sendInviteToUsers(roomId, userIds)
    }

    const userIdObservables = userIds.map((apiUserId) =>
      this.getRocketChatUserId(apiUserId).pipe(
        map((response) => response?.user?._id)
      )
    )

    return forkJoin(userIdObservables).pipe(
      switchMap((rocketChatUserIds: string[]) => {
        const validUserIds = rocketChatUserIds.filter(Boolean)
        return this.sendInviteToUsers(roomId, validUserIds)
      })
    )
  }

  removeMemberFromRoom(
    roomId: string,
    userId: string
  ): Observable<{ success: boolean }> {
    const url = environment.chatBasePath + '/channels.kick'
    const data = {
      roomId,
      userId,
    }
    const resultObservable = this.httpClient.post<any>(url, data, {
      headers: this.getAuthHeaders(),
    })
    return resultObservable
  }

  private sendInviteToUsers(
    roomId: string,
    userIds: string[]
  ): Observable<{ success: boolean }> {
    const url = environment.chatBasePath + '/channels.invite'
    const data = {
      roomId,
      userIds,
    }
    const resultObservable = this.httpClient.post<any>(url, data, {
      headers: this.getAuthHeaders(),
    })
    return resultObservable
  }

  private loadUniversityUsers(
    universityId: string
  ): Observable<UniversityUserList> {
    const observable = this.universityService.universityUsers.pipe(
      first((universityUsers) => universityUsers?.universityId === universityId)
    )
    this.universityService.fetchUniversityUserList(universityId)
    return observable
  }

  private createChatRoomWithData(data: {
    name: string
    members: string[]
    customFields: { [key: string]: string }
  }): Observable<{ success: boolean; chatRoomId: string }> {
    const url = environment.chatBasePath + '/channels.create'
    const resultObservable = this.httpClient.post<any>(url, data, {
      headers: this.getAuthHeaders(),
    })

    const observable = resultObservable.pipe(
      map((result) => {
        this.fetchChatRooms()
        if (result && result.success && result.channel) {
          setTimeout(() => {
            this.unreadMessagesService.addUnreadRoomInfo(result.channel._id)
            this.addMembersAsOwners(result.channel._id)
          }, 2000)
          return { success: true, chatRoomId: result.channel._id }
        }
        return { success: false, chatRoomId: undefined }
      })
    )
    observable.pipe(take(1)).subscribe()

    return observable
  }

  private addMembersAsOwners(roomId: string) {
    this.loadMembers(roomId)
      .pipe(take(1))
      .subscribe((memberIds) => {
        memberIds.forEach((memberId) => {
          this.addRoomOwner(roomId, memberId)
        })
      })
  }

  private loadMembers(roomId: string): Observable<string[]> {
    const url = environment.chatBasePath + '/channels.members?roomId=' + roomId
    return this.httpClient
      .get<any>(url, { headers: this.getAuthHeaders() })
      .pipe(
        map((response) => {
          return response.members.map((member: any) => member._id)
        })
      )
  }

  private addRoomOwner(roomId: string, userId: string): Observable<boolean> {
    if (!this.chatUserData) {
      return of(false)
    }

    const url = environment.chatBasePath + '/channels.addOwner'
    const postData = {
      roomId: roomId,
      userId: userId,
    }

    const observable = this.httpClient
      .post<any>(url, postData, { headers: this.getAuthHeaders() })
      .pipe(
        map((result) => {
          this.fetchChatRooms()
          return result.success
        })
      )
    observable.pipe(take(1)).subscribe()
    return observable
  }

  leaveRoom(roomId: string): Observable<boolean> {
    if (!this.chatUserData) {
      return of(false)
    }

    const url = environment.chatBasePath + '/channels.leave'
    const postData = {
      roomId: roomId,
    }

    return this.httpClient
      .post<any>(url, postData, { headers: this.getAuthHeaders() })
      .pipe(
        map((result) => {
          this.fetchChatRooms()
          this.leftChatRoom.next(roomId)
          return result.success
        })
      )
  }

  async groupRoomsByActiveJob(rooms: ChatRoom[]): Promise<GroupedChatRooms> {
    this.status.next(RestServiceStatus.Loading)

    const activeJobChats: ChatRoom[] = []
    const inactiveJobChats: ChatRoom[] = []

    const now = new Date().getTime()

    const jobPromises = rooms.map(async (room) => {
      const jobId = room.getJobId()
      const job = await firstValueFrom(
        this.jobService
          .fetchJob(jobId)
          .pipe(
            switchMap(() =>
              this.jobService.job.pipe(first((j) => j?.jobId === jobId))
            )
          )
      )
      return { room, job }
    })

    const results = await Promise.all(jobPromises)

    results.forEach(({ room, job }) => {
      const deadline = new Date(job.applicationDeadline).getTime()
      if (deadline > now) {
        activeJobChats.push(room)
      } else {
        inactiveJobChats.push(room)
      }
    })

    this.status.next(RestServiceStatus.Success)
    return {
      chatRoomsWithActiveJob: this.sortRoomsByDate(activeJobChats),
      chatRoomsWithInactiveJob: this.sortRoomsByDate(inactiveJobChats),
    }
  }

  private sortRoomsByDate(rooms: ChatRoom[]): ChatRoom[] {
    return rooms
      .filter((chatRoom) => chatRoom.getUrl().length > 0)
      .sort((a, b) => {
        // sort by last message or creation timestamp
        const aRawTimestamp = a.lastMessage?.timestamp ?? a.timestamp
        const bRawTimestamp = b.lastMessage?.timestamp ?? b.timestamp
        if (aRawTimestamp && bRawTimestamp) {
          const aTimestamp = new Date(aRawTimestamp).getTime()
          const bTimestamp = new Date(bRawTimestamp).getTime()
          return aTimestamp > bTimestamp ? -1 : 1
        }
        return aRawTimestamp !== undefined ? -1 : 1
      })
  }
}
